import { Component, OnInit, inject } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { Advertiser, CampaignsAdvertiserAdminApiClient, CreateAdvertiserRequestDto } from 'src/app/api/api-campaigns.service.generated';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ButtonComponent, PhotoComponent } from 'wecheer-common';
import { lastValueFrom } from 'rxjs';
import { CountryCode } from 'src/app/advertiser/advertiser-details/advertiser-campaigns/_/campaign-base-edit/country-code';

@Component({
  selector: 'app-advertiser-edit',
  templateUrl: './advertiser-edit.component.html',
  styleUrls: ['./advertiser-edit.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    ButtonComponent,
    PhotoComponent
]
})
export class AdvertiserEditComponent implements OnInit {
  private dialog = inject(MatDialog);
  private dialogRef = inject<MatDialogRef<AdvertiserEditComponent, Advertiser | undefined>>(MatDialogRef);
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  private snackBar = inject(MatSnackBar);
  editModel = inject<Advertiser>(MAT_DIALOG_DATA);


  static config = { width: '600px' };

  form: FormGroup = new FormGroup({
    name: new FormControl('', Validators.required),
    countries: new FormControl<string[]>([], Validators.required),
    description: new FormControl(''),
    appToRedirect: new FormControl('')
  });

  allCountries: { iso2: string; name: string }[] = [];

  isLoading = false;
  isAdding = true;
  photoUrl: string;
  uploadPhotoKey: string;
  ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];

  constructor() {
    const editModel = this.editModel;


    this.allCountries = new CountryCode().allCountries.map(c => ({ iso2: c[1].toString(), name: c[0].toString() }));

    if (editModel) {
      this.isAdding = false;
      this.form.patchValue({
        name: editModel.name,
        countries: editModel.countries || [],
        description: editModel.description,
        appToRedirect: editModel.appToRedirect
      });
      this.photoUrl = editModel.photoThumb;
    }
  }

  async ngOnInit() {
  }

  async onSubmit() {

    const model = new CreateAdvertiserRequestDto({
      advertiser: new Advertiser({
        ...this.editModel,
        name: this.form.value.name,
        countries: this.form.value.countries,
        description: this.form.value.description,
        appToRedirect: this.form.value.appToRedirect
      }),
      uploadKey: this.uploadPhotoKey
    });

    if (model.advertiser.name.toString().trim() === '') {
      this.snackBar.open(`Invalid advertiser name`, null, { duration: 3000 });
      return;
    }

    this.isLoading = true;
    try {
      const o = await lastValueFrom(this.advertiserApi.createOrUpdateAdvertiser(model));

      this.snackBar.open(`Advertiser ${model.advertiser.name} was saved`, null, { duration: 3000 });
      this.dialogRef.close(o);
    } catch (err: any) {
      if (err.status === 400 && err.response)
        new MessageDialogModel(err.response).show(this.dialog);
    }
    this.isLoading = false;
  }

  async onAvatarImageSelected(event: any) {
    try {
      const file = event.target.files[0];
      if (!file || !this.isValidFile(file)) {
        this.snackBar.open('Invalid image file', 'Dismiss', { duration: 2000 });
        return;
      }

      this.uploadPhotoKey = GUID.newGuid().replace(/-/g, '');
      const uploadUrl = await lastValueFrom(this.advertiserApi.getAdvertiserUploadUrl(this.uploadPhotoKey));
      const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl, file));
      if (uploadSuccess) {
        const parts = uploadUrl.split('?');
        const photoUrl = parts[0];
        this.photoUrl = photoUrl.replace('s3.ap-southeast-1.amazonaws.com/', ''); // trim s3 part to get photo using cloudfront
      }
    } catch (err) {
      console.error('upload failed', err);
    }
  }

  isValidFile(file: File): boolean {
    return file.name && this.ALLOWED_EXTENSIONS.filter(e => file.name.trim().toLowerCase().endsWith(e)).length > 0;
  }

  onClose(): void {
    this.dialogRef.close(undefined);
  }

}
